<template>
  <v-container fluid class="pa-2">
    <v-row>
      <v-col>
        <div v-if="details">
          <div v-if="type" class="ml-13 text-size">
            {{ type }}
          </div>
          <h1 class="icon-title-container">
            <AssetIcon
              v-if="iconSrc && iconColor"
              :icon="iconSrc"
              :color="iconColor"
            ></AssetIcon>
            <div class="title-header">{{ title }}</div>
            <div class="clearfix"></div>
          </h1>
          <div
            v-if="details.country && details.country.name"
            class="ml-13 mb-2 text-size"
          >
            {{ details.country.name }}
          </div>
          <Loader v-if="!details" text="" color="white"></Loader>
          <div v-if="monitorRadius" class="mb-3 monitor-style">
            <span class="application-heading">Monitoring Proximity:</span>
            {{ monitorRadius }}
          </div>
          <div>
            <v-toolbar flat :height="44" class="layout-toolbar">
              <v-tabs
                v-model="tab"
                background-color="transparent"
                height="40"
                class="layout-tabs"
              >
                <v-tab key="cr_incidents" class="px-0 tab-text">
                  {{ labels.cr_incidents }}
                </v-tab>
                <v-tab key="ratings" class="px-0 tab-text">
                  {{ labels.ratings }}
                </v-tab>
                <v-tab key="asset_information" class="px-0 tab-text">
                  {{ labels.asset_information }}
                </v-tab>
              </v-tabs>
            </v-toolbar>

            <v-tabs-items dark v-model="tab">
              <v-tab-item key="cr_incidents"> </v-tab-item>
              <v-tab-item key="ratings">
                <AssetsPaneRatings :country-code="country.countryAlpha2Code">
                </AssetsPaneRatings>
              </v-tab-item>
              <v-tab-item key="asset_information">
                <div v-if="details">
                  <AssetsPaneDetails :asset-details="details">
                  </AssetsPaneDetails>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { LABELS } from "@/common/constants/labels";
import lodashGet from "lodash/get";
import { formatDisplayMonitorRadius } from "@/common/basic.utilities";
import { circle } from "@turf/turf";
import { getDefaultPolygonOptions } from "@/common/maps/styles";
import { mapPolygonData, removeMapData } from "@/common/maps/features";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP,
} from "@/store";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getAssetById } from "@/services/assetManagement/assets.service";

export default {
  name: "AssetsPane",
  components: {
    Loader: () => import("@/components/common/Loader"),
    AssetIcon: () => import("@/components/assetManagement/AssetIcon"),
    AssetsPaneRatings: () =>
      import("@/components/assetManagement/AssetsPaneRatings"),
    AssetsPaneDetails: () =>
      import("@/components/assetManagement/AssetsPaneDetails"),
  },
  data() {
    const headlineTabs = {
      cr_incidents: "cr_incidents",
      asset_information: "asset_information",
      ratings: "ratings",
    };

    const selectedTab = headlineTabs.cr_incidents;

    return {
      headlineTabs: headlineTabs,
      tab: selectedTab,
      labels: LABELS,
      details: null,
      id: null,
    };
  },
  computed: {
    ...mapGetters({
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      preferences: MODULE_PREFIX_USER + "preferences",
      maps: MODULE_PREFIX_MAP + "maps",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    unit() {
      return this.preferences.poisDistUnits === "miles" ? "mi" : "km";
    },
    title() {
      if (this.details?.title) {
        return this.details.title;
      }
      return null;
    },
    monitorRadius() {
      if (this.details?.monitorRadius) {
        return `${formatDisplayMonitorRadius(
          this.details.monitorRadius,
          this.preferences?.poisDistUnits
        )} ${this.unit}`;
      }
      return null;
    },
    country() {
      if (this.details?.country) {
        return this.details.country;
      }
      return null;
    },
    type() {
      if (this.details?.assetType?.title) {
        return this.details.assetType.title;
      }
      return null;
    },
    iconColor() {
      if (this.details?.assetType?.color) {
        return this.details.assetType.color;
      }
      return null;
    },
    iconSrc() {
      if (this.details?.assetType?.icon) {
        return this.details.assetType.icon;
      }
      return null;
    },
  },
  async mounted() {
    if (
      this.detailsSidebar.selected === DETAIL_SIDEBAR_TYPES.assets &&
      lodashGet(this.detailsSidebar, "features[0].properties.gid")
    ) {
      this.id = this.detailsSidebar.features[0].properties.gid;
      this.getDetails();
    }
  },
  methods: {
    async getDetails() {
      this.details = await getAssetById(this.id);
      this.showAssetMonitoringProximity();
    },
    getCustomFieldLabel(label) {
      return label ? upperCaseFirstLetter(label) : label;
    },
    showAssetMonitoringProximity() {
      if (
        this.details?.longitude &&
        this.details?.latitude &&
        this.details?.monitorRadius
      ) {
        const assetProximityFeature = circle(
          [this.details.longitude, this.details.latitude],
          this.details.monitorRadius,
          {
            steps: 64,
            units: "kilometers",
          }
        );

        const assetProximitySource = {
          type: "FeatureCollection",
          features: [assetProximityFeature],
        };

        const assetProximityParams = {
          id: "asset-proximity",
          opacity: 0.3,
        };

        mapPolygonData({
          id: assetProximityParams.id,
          data: assetProximitySource,
          polygonOptions: getDefaultPolygonOptions(assetProximityParams),
          map: this.map,
        });
      }
    },
    hideAssetMonitoringProximity() {
      removeMapData(this.map, "asset-proximity");
    },
  },
  beforeDestroy() {
    this.details = null;
    this.id = null;
    this.hideAssetMonitoringProximity();
  },
};
</script>

<style scoped>
.icon-title-container {
  display: flex;
  align-items: center; /* Vertically centers the text with the icon */
}
.icon-title-container .title {
  margin-left: 8px; /* Adjust the space between the icon and the text */
  white-space: normal;
  word-wrap: break-word;
  flex: 1;
}
.layout-toolbar .v-toolbar__content {
  padding-left: 12px !important;
  padding-right: 1px !important;
}
.layout-tabs .v-tab {
  padding-left: 18px !important;
  padding-right: 18px !important;
}
.tab-text {
  font-size: 14px !important;
}
.title-header {
  font-size: 24px;
  font-weight: bold;
}
.text-size {
  font-size: 13px;
}
.monitor-style {
  font-weight: bold;
}
</style>
